import {
  NavLink as ReactRouterDomNavLink
} from 'react-router-dom';
import Core from '../../../lib/Core';
import {
  NOT
} from '../../../lib/GenericTools.lib';
import {
  trim
} from '../../../lib/String.lib';
import {
  matchLocation,
  reloadLocation
} from '../../../lib/URL.lib';
import {
  joinClassName,
  mapWrapperName
} from '../Libraries/Theme.lib';
import Icon from './Icon';
import IconButton from './IconButton';
import Menu from './Menu';
import Typography from './Typography';
import Wrapper from './Wrapper';

/**
 * Renders a customizable navigation link with optional icon, menu, and styling.
 *
 * @function NavLink
 * @param {Object} props - Component props.
 * @param {React.Component} [props.component=ReactRouterDomNavLink] - Wrapper component (e.g., NavLink from react-router-dom).
 * @param {string} props.to - Navigation target path.
 * @param {boolean} [props.exact=false] - Whether to match the route exactly.
 * @param {string} [props.className=''] - Additional wrapper class names.
 * @param {string} [props.title] - Optional tooltip title.
 * @param {Object} [props.titleStyle] - Optional tooltip style.
 * @param {string} [props.placement] - Tooltip placement.
 * @param {React.ReactNode} [props.children] - Child content inside the link.
 * @param {string} [props.label=''] - Label text if children not provided.
 * @param {string} [props.path=''] - Fallback path.
 * @param {boolean} [props.acl=true] - Access control flag; hides link if false.
 * @param {boolean|string} [props.icon=false] - Optional icon component or name.
 * @param {boolean} [props.main=false] - Main navigation styling flag.
 * @param {boolean} [props.menu=false] - Menu item styling flag.
 * @param {boolean} [props.divider=true] - Menu divider flag.
 * @param {boolean} [props.decoration=false] - Remove text decoration if true.
 * @param {boolean} [props.noHover=false] - Disable hover effects if true.
 * @param {boolean} [props.noSelected=false] - Prevent selected styling if true.
 * @param {boolean} [props.reload=false] - Reload page on click.
 * @param {boolean} [props.openInNewTab=false] - Open link in new tab if true.
 * @returns {JSX.Element|null} Rendered navigation link or null if ACL fails.
 *
 * @description
 * - Computes dynamic class names based on props (main, menu, icon, selected, hover, decoration).
 * - Wraps label or children in `<Typography>`.
 * - Prepends an icon if specified.
 * - Wraps in a `<Menu.Item>` if `menu` is true.
 * - Uses a `Wrapper` component to render the final element with proper routing.
 * - Optional `reload` triggers a page reload when clicked.
 *
 * @see https://reactrouter.com/en/main/components/nav-link
 */
export default function NavLink({
  component = ReactRouterDomNavLink,
  to,
  exact = false,
  className = '',
  title,
  titleStyle,
  placement,
  children,
  label = '',
  path = '',
  acl = true,
  icon = false,
  main = false,
  menu = false,
  divider = true,
  decoration = false,
  noHover = false,
  noSelected = false,
  reload = false,
  openInNewTab = false,
  ...props
}) {
  if (!acl) { return null; }
  to = trim(to) || trim(path) || trim(label).toLowerCase();
  children = children || label;

  const _isSelected = noSelected || matchLocation(to);
  const _yesHover = NOT(noHover);
  const _noDecoration = NOT(decoration);
  const _wrapperClassName = [
    _yesHover && 'c-purple-hover',
    _noDecoration && 'decoration-none',
    className
  ];
  const _menuClassName = ['flex-row flex-align-left-center py-1 px-2'];
  const _iconClassName = ['c-inherit'];
  const _labelClassName = ['c-inherit-all'];

  if (main || menu) {
    if (main) {
      _wrapperClassName.push('flex-column flex-align-center m-1 mx-5');
      _labelClassName.push('f-sm mt-05');
    }
    else if (menu) {
      if (divider) {
        _menuClassName.push('border-bottom');
      }
      if (icon) {
        _iconClassName.push('mr-3');
      }
    }
    if (_isSelected) {
      _wrapperClassName.push('c-purple fw-700');
    }
    else if (menu) {
      _wrapperClassName.push('c-black-dark fw-700');
    }
    else {
      _wrapperClassName.push('c-black-medium fw-700');
    }
  }
  else {
    _wrapperClassName.push('inline-block c-purple f-md fw-500');
  }

  children = (
    <Typography className={joinClassName(_labelClassName)}>
      {children}
    </Typography>
  );

  if (icon) {
    if (label) {
      children = (
        <>
          <Icon
            icon={icon}
            className={joinClassName(_iconClassName)}
          />
          {children}
        </>
      );
    }
    else {
      _iconClassName.push('c-black-medium');
      children = (
        <IconButton
          icon={icon}
          className={joinClassName(_iconClassName)}
        />
      );
    }
  }

  if (menu) {
    children = (
      <Menu.Item
        className={joinClassName(_menuClassName)}
      >
        {children}
      </Menu.Item>
    );
  }

  const _wrapperProps = mapWrapperName(
    'NavLink',
    {
      ...props,
      component,
      to: exact ? to : Core.getLink(to),
      className: joinClassName(_wrapperClassName),
      title,
      titleStyle,
      placement,
      children
    }
  );

  if (reload) {
    _wrapperProps.onClick = (event) => setTimeout(() => reloadLocation())
  }

  if (openInNewTab) {
    _wrapperProps.onClick = (e) => {
      e.preventDefault();
      window.open(
        `#${to.replace('#', '')}`,
        '_blank',
        'noopener,noreferrer'
      );
    };
  }

  return (
    <Wrapper {..._wrapperProps} />
  );
}
